import { ProethicaPrinciplesTabs } from "@features/proethica-principles";
import {
	ProethicsReflectItem,
	ProethicsSimulationItem,
	ScenarioPractice,
} from "@features/proethics-simulation";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { Box, useDevice } from "@memorang/ui";
import RenderMarkdown from "@memorang/ui/src/components/MarkdownRenderer";
import { WorkflowItem } from "components/WorkflowItem/WorkflowItem";
import { useTranslation } from "react-i18next";
import { Text, TouchableRipple } from "react-native-paper";
import useSessionStore from "../../../hooks/useSessionStore";
import ItemWrapper from "../../ItemWrapper";
import AnalysisTabs from "./variants/AnalysisTabs";
import BinaryChoice from "./variants/BinaryChoice";
import EthicalSituationCards from "./variants/EthicalSituationCards";
import ExploreAccordion from "./variants/ExploreAccordion";
import Flashcards from "./variants/Flashcards";
import Lesson from "./variants/Lesson";
import MCQ from "./variants/MCQ";
import ModuleLanding from "./variants/ModuleLanding";
import PerspectiveFlipGrid from "./variants/PerspectiveFlipGrid";
import PrinciplesAccordion from "./variants/PrinciplesAccordion";
import ReviewFlipGrid from "./variants/ReviewFlipGrid";
import Scenario from "./variants/Scenario";
import ScenarioStepsAccordion from "./variants/ScenarioStepsAccordion";
import SliderChoice from "./variants/SliderChoice";

interface Props {
	item: BaseItemSchema;
	originalItemType: OriginalItemTypes;
	mappedItemType: RenderableItemTypes;
	selectedChoiceIds: string[] | undefined;
	correctAnswerIds: string[] | undefined;
	crossedOutChoiceIds: string[] | undefined;
	isAnswered?: boolean;
	isSummativeTest?: boolean;
	onSelectChoice: (choiceId: string, crossingOut?: boolean) => void;
	currentUserResponse?: unknown;
	toggleShowReadMore?: () => void;
	handleHighlightedTextPress?: () => void;
	correctAnswerOrders?: string[];
	inReview?: boolean;
}

const BaseItemType = ({
	item,
	selectedChoiceIds,
	correctAnswerIds,
	crossedOutChoiceIds,
	isAnswered,
	isSummativeTest,
	onSelectChoice,
	toggleShowReadMore,
	originalItemType,
	mappedItemType,
	handleHighlightedTextPress,
	correctAnswerOrders,
	inReview,
}: Props) => {
	const { isMobile } = useDevice();

	const { content } = item;

	const multiSelect = mappedItemType === "SATA";

	const showHint = !(isSummativeTest || isAnswered);
	const defaultInstructionByType: Record<string, string> = {
		SCENARIO: "",
		SLIDER_CHOICE: "Drag the slider to your position",
		FLASHCARD: "Tap a card to flip",
	};
	const instructions =
		content?.instruction || defaultInstructionByType[mappedItemType];

	const updateItemStates = useSessionStore((state) => state.updateItemStates);

	const showButtonOpenPassage = item.metadata?.type === "INSERT_TEXT";

	const theme = useAppTheme();
	const hasInserted = !!selectedChoiceIds?.length;

	const { t } = useTranslation();

	const ButtonOpenPassage = () => {
		return (
			<TouchableRipple
				onPress={isMobile ? toggleShowReadMore : undefined}
				pointerEvents={isMobile ? "auto" : "none"}
				style={{
					alignItems: "center",
					margin: isMobile ? 16 : 0,
					padding: 12,
					borderRadius: theme.roundness,
					borderWidth: 1,
					backgroundColor: hasInserted ? theme.colors.primary : "transparent",
					borderColor: theme.colors.primary,
				}}
			>
				<Box
					flexDirection="row"
					width="100%"
					justifyContent="space-between"
					alignItems="center"
				>
					<Text
						style={{
							color: theme.colors[hasInserted ? "white" : "onSurfaceVariant"],
						}}
						variant="labelLarge"
					>
						{hasInserted
							? t("session.passageStem.insertedAtPosition", {
									position:
										(item.content?.choices?.findIndex((choice) =>
											selectedChoiceIds.includes(choice.id),
										) ?? 0) + 1,
								})
							: t("session.passageStem.notYetInserted")}
					</Text>
					{isMobile && (
						<Text
							variant="labelMedium"
							style={{
								textTransform: "uppercase",
								color: theme.colors[hasInserted ? "white" : "primary"],
							}}
						>
							{t("session.passageStem.openPassage")}
						</Text>
					)}
				</Box>
			</TouchableRipple>
		);
	};
	const isExploreAccordion = mappedItemType === "EXPLORE_ACCORDION";
	const isReadOnlyExplore =
		isExploreAccordion &&
		!content?.choices?.length &&
		Boolean(content?.principles?.length);
	const leftPanel =
		isExploreAccordion && !isReadOnlyExplore && content?.principles?.length ? (
			<Box flex={1}>
				{(content.stem || content.instruction) && (
					<Box gap={6} paddingBottom={16}>
						{content.stem ? (
							<RenderMarkdown fontSize={16} text={content.stem} />
						) : null}
						{content.instruction ? (
							<RenderMarkdown
								fontSize={16}
								text={content.instruction}
								paragraphMarginVertical={0}
							/>
						) : null}
					</Box>
				)}
				<PrinciplesAccordion
					principles={content.principles}
					inReview={inReview}
				/>
			</Box>
		) : undefined;

	return (
		<ItemWrapper
			item={item}
			instructions={instructions}
			toggleShowReadMore={toggleShowReadMore}
			originalItemType={originalItemType}
			mappedItemType={mappedItemType}
			showInstructions={!isExploreAccordion}
			handleHighlightedTextPress={handleHighlightedTextPress}
			selectedChoiceIds={selectedChoiceIds}
			leftPanel={leftPanel}
			suppressPassageLayout={Boolean(isReadOnlyExplore)}
		>
			{showButtonOpenPassage && <ButtonOpenPassage />}

			{(() => {
				switch (mappedItemType) {
					case "MCQ":
					case "SATA":
					case "SEQUENCE":
						return (
							<MCQ
								multiSelect={multiSelect}
								isSummativeTest={isSummativeTest}
								isAnswered={isAnswered}
								selectedChoiceIds={selectedChoiceIds}
								correctAnswerIds={correctAnswerIds}
								crossedOutChoiceIds={crossedOutChoiceIds}
								choices={content?.choices ?? []}
								currentItem={item}
								handleSelectChoice={onSelectChoice}
								showHint={showHint}
								isMobile={isMobile}
								mappedItemType={mappedItemType}
								correctAnswerOrders={correctAnswerOrders}
							/>
						);

					case "WORKFLOW_GRAPH":
						return isWeb ? (
							<Box height={800} width={"100%"}>
								<WorkflowItem
									id={item.id}
									initialNodes={item.metadata?.workflow?.initialNodes ?? []}
									initialEdges={item.metadata?.workflow?.initialEdges ?? []}
									correctNodes={item.metadata?.workflow?.correctNodes ?? []}
									correctEdges={item.metadata?.workflow?.correctEdges ?? []}
									isAnswered={isAnswered}
								/>
							</Box>
						) : null;
					case "SCENARIO": {
						const taskType = item.metadata?.taskType as string | undefined;
						if (taskType === "AI_SIMULATION") {
							return (
								<ProethicsSimulationItem
									scenarioId={item.metadata?.scenarioId}
								/>
							);
						}
						if (taskType === "AI_SIMULATION_REPORT") {
							return (
								<ProethicsReflectItem
									scenarioId={item.metadata?.scenarioId}
									title={item.metadata?.title}
								/>
							);
						}
						if (taskType === "MODULE_LANDING" && content?.moduleLanding) {
							return <ModuleLanding data={content.moduleLanding} />;
						}
						if (taskType === "LESSON") {
							return <Lesson text={content?.lessonText ?? ""} />;
						}
						if (taskType === "SCENARIO_STEPS" && content?.steps?.length) {
							return (
								<ScenarioStepsAccordion
									steps={content.steps}
									inReview={inReview}
								/>
							);
						}
						if (content?.scenarioPractice) {
							return <ScenarioPractice {...content.scenarioPractice} />;
						}
						if (content?.textAfter) {
							return (
								<Box gap={16}>
									<Scenario media={content?.media} inReview={inReview} />
									<RenderMarkdown
										fontSize={16}
										text={content.textAfter}
										paragraphMarginVertical={0}
									/>
								</Box>
							);
						}
						return <Scenario media={content?.media} inReview={inReview} />;
					}
					case "SLIDER_CHOICE":
						return content?.sliderConfig ? (
							<SliderChoice
								choices={content.choices ?? []}
								sliderConfig={content.sliderConfig}
								isAnswered={isAnswered}
								itemId={item.id!}
								storedValue={(() => {
									const stored =
										item.id != null
											? useSessionStore.getState().itemStates?.get(item.id)
													?.currentOrders?.[0]
											: undefined;
									return stored != null ? Number(stored) : undefined;
								})()}
								onAnswer={(value) => {
									const config = content.sliderConfig!;
									const snapValues = config.snapPoints.map((s) => s.value);
									const minV = Math.min(...snapValues);
									const maxV = Math.max(...snapValues);
									const midV = (minV + maxV) / 2;
									const sideChoiceId =
										value <= midV
											? (config.leftChoiceId ?? content.choices?.[0]?.id)
											: (config.rightChoiceId ?? content.choices?.[1]?.id);
									if (sideChoiceId) {
										updateItemStates({
											itemId: item.id!,
											selectedChoiceId: sideChoiceId,
											singleReplace: true,
											currentOrders: [String(value)],
										});
									}
								}}
							/>
						) : null;
					case "FLASHCARD": {
						const taskType = item.metadata?.taskType;
						const onAllViewed = () => {
							if (!isAnswered) {
								updateItemStates({
									itemId: item.id!,
									selectedChoiceId: "viewed",
									answered: true,
								});
							}
						};
						if (
							taskType === "ETHICAL_SITUATION" &&
							content?.situations?.length
						) {
							return (
								<EthicalSituationCards
									key={item.id}
									situations={content.situations}
									situationIndex={content.situationIndex}
									situationTotal={content.situationTotal}
									onAllViewed={onAllViewed}
									inReview={inReview}
								/>
							);
						}
						if (taskType === "PERSPECTIVE_GRID" && content?.cards) {
							return (
								<PerspectiveFlipGrid
									cards={content.cards}
									onAllViewed={onAllViewed}
									inReview={inReview}
								/>
							);
						}
						if (taskType === "REVIEW_GRID" && content?.cards) {
							const sessionItems = useSessionStore.getState().sessionItems;
							const itemIndex = sessionItems.findIndex((s) => s.id === item.id);
							const exploreItem = sessionItems
								.slice(0, itemIndex)
								.reverse()
								.find(
									(s) =>
										s.metadata?.type === "EXPLORE_ACCORDION" &&
										s.content?.choices?.length,
								);
							const exploreSelectedIds =
								exploreItem?.id != null
									? (useSessionStore.getState().itemStates?.get(exploreItem.id)
											?.selectedChoiceIds ?? [])
									: [];
							return (
								<ReviewFlipGrid
									cards={content.cards}
									choices={exploreItem?.content?.choices ?? []}
									selectedChoiceIds={exploreSelectedIds}
								/>
							);
						}
						if (taskType === "ANALYSIS_TABS" && content?.cards) {
							return (
								<AnalysisTabs
									cards={content.cards}
									itemId={item.id!}
									onAllViewed={onAllViewed}
									inReview={inReview}
								/>
							);
						}
						return content?.cards ? (
							<Flashcards
								cards={content.cards}
								itemId={item.id!}
								onAllViewed={onAllViewed}
							/>
						) : null;
					}
					case "BINARY_CHOICE":
						return (
							<BinaryChoice
								choices={content?.choices ?? []}
								isAnswered={isAnswered ?? false}
								selectedChoiceIds={selectedChoiceIds ?? []}
								correctAnswerIds={correctAnswerIds ?? []}
								itemId={item.id!}
								onSelect={(id) => onSelectChoice(id)}
							/>
						);
					case "EXPLORE_ACCORDION": {
						const onAllPrinciplesViewed = () => {
							if (!isAnswered) {
								updateItemStates({
									itemId: item.id!,
									selectedChoiceId: "viewed",
									answered: true,
								});
							}
						};
						return (
							<Box gap={12}>
								{isReadOnlyExplore ? (
									<Box gap={6}>
										{content?.stem ? (
											<RenderMarkdown fontSize={16} text={content.stem} />
										) : null}
										{content?.instruction ? (
											<RenderMarkdown
												fontSize={16}
												text={content.instruction}
												paragraphMarginVertical={0}
											/>
										) : null}
										{content?.principlesLayout === "tabs" ? (
											<ProethicaPrinciplesTabs
												principles={content?.principles ?? []}
												onAllViewed={onAllPrinciplesViewed}
												inReview={inReview}
											/>
										) : (
											<PrinciplesAccordion
												principles={content?.principles ?? []}
												inReview={inReview}
											/>
										)}
									</Box>
								) : null}
								{content?.questionStem ? (
									<Text variant="bodyLarge" style={{ fontWeight: "bold" }}>
										{content.questionStem}
									</Text>
								) : null}
								{isReadOnlyExplore ? null : (
									<ExploreAccordion
										choices={content?.choices ?? []}
										isAnswered={isAnswered ?? false}
										selectedChoiceIds={selectedChoiceIds ?? []}
										correctAnswerIds={correctAnswerIds ?? []}
										itemId={item.id!}
										handleSelectChoice={onSelectChoice}
									/>
								)}
							</Box>
						);
					}
					default:
						return null;
				}
			})()}
		</ItemWrapper>
	);
};

export default BaseItemType;
